/**
 * Capacitor on-device video compression bridge (v1).
 *
 * Large video uploads fail more often on slow/unreliable mobile connections
 * simply because the file is big. This module lets the native shell shrink a
 * picked video before it is handed to the uploader, using a native
 * `VideoCompressor` plugin that lives in the separate Capacitor shell repo
 * (not this one) — see docs/native/video_compressor_plugin.md for its
 * contract.
 *
 * This is a strict no-op everywhere else: outside the Capacitor shell, when
 * the native plugin is not registered, or when the `video_compression`
 * feature flag is off, `compressVideoBestEffort` returns the original file
 * untouched. Even when all three gates are open, compression is
 * best-effort only. It can NEVER make an upload worse than skipping it:
 * every failure mode (probe error, compress error, timeout, or a result
 * that fails post-compress validation) falls back to the original file. The
 * worst case is "no improvement," never a regression.
 *
 * Unlike push_registration.js and background_upload.js, there is no npm
 * package to dynamically import here — the native plugin has no published
 * JS wrapper (it is bundled only inside the native shell), so it is reached
 * the same way detection.js reaches window.Capacitor itself: through the
 * injected `window.Capacitor.Plugins.VideoCompressor` global. That means
 * there is nothing to lazy-load; the plugin reference is only ever read
 * behind isCapacitorEnvironment() + isCapacitorPluginAvailable(), so it is
 * never touched outside the shell.
 *
 * Mirrors the rest of the capacitor/ modules: guards up front, a single
 * `reportError -> Honeybadger.notify(..,
 * {context:{component:"capacitor_video_compress"}})`, and never throws out
 * to the caller.
 */

import Honeybadger from "@honeybadger-io/js";
import {
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
  capacitorPlatform,
} from "./detection";

// The native plugin's registerPlugin() name, not an npm package name — see
// the module doc comment above.
const VIDEO_COMPRESSOR_PLUGIN_NAME = "VideoCompressor";

const VIDEO_COMPRESSION_FLAG = "video_compression";

// v1 trigger-gate thresholds (docs/native/video_compressor_plugin.md and
// BUILD_SPEC.md carry the reasoning for each).
const BELOW_SIZE_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB
const MIN_DURATION_MS = 5000; // 5s
const MAX_FPS_FOR_COMPRESSION = 60; // slow-mo / velocity capture is exempt above this
const MAX_DIMENSION = 1440; // cap the long edge; never upscale

// Hard timeout for the native compress() call. Generous because a large
// mezzanine-grade transcode can legitimately take a while, but bounded so a
// stuck native call can never hang the upload flow forever.
const COMPRESS_TIMEOUT_MS = 90000;

// targetBitrateFor() lookup. Two long-edge tiers (1440 / 1080-and-below), each
// split on whether the source frame rate is above 30fps (higher fps needs
// more bits per second to hold the same perceived quality).
const HIGH_FPS_THRESHOLD = 30;
const LOW_TIER_MAX_LONG_EDGE = 1080;
const TARGET_BITRATE_1440_HIGH_FPS_BPS = 24_000_000;
const TARGET_BITRATE_1440_STANDARD_BPS = 16_000_000;
const TARGET_BITRATE_1080_HIGH_FPS_BPS = 18_000_000;
const TARGET_BITRATE_1080_STANDARD_BPS = 11_000_000;

// Sentinel distinguishing "the timeout won the race" from an actual resolved
// compress() result (which is always a plain object, never this symbol).
const COMPRESS_TIMEOUT = Symbol("video_compress_timeout");

/**
 * Mezzanine-grade target bitrate (bps) for a given output long edge and
 * source frame rate. Kept as a small, documented lookup rather than a
 * formula so the numbers can be tuned per tier without touching call sites.
 * @param {number} longEdge - output long edge in px (post max-dimension cap)
 * @param {number} fps
 * @returns {number} bps
 */
function targetBitrateFor(longEdge, fps) {
  const highFps = fps > HIGH_FPS_THRESHOLD;
  if (longEdge > LOW_TIER_MAX_LONG_EDGE) {
    return highFps
      ? TARGET_BITRATE_1440_HIGH_FPS_BPS
      : TARGET_BITRATE_1440_STANDARD_BPS;
  }
  return highFps
    ? TARGET_BITRATE_1080_HIGH_FPS_BPS
    : TARGET_BITRATE_1080_STANDARD_BPS;
}

/**
 * Whether the given feature flag is enabled for the current user. Rails sets
 * window._tkcEnabledFeatures in the layout (shared with the rest of the
 * Capacitor bridge and the main app).
 * @param {string} name
 * @returns {boolean}
 */
function flagOn(name) {
  try {
    return (
      typeof window !== "undefined" &&
      Array.isArray(window._tkcEnabledFeatures) &&
      window._tkcEnabledFeatures.includes(name)
    );
  } catch {
    return false;
  }
}

/**
 * Report a compression failure so it's visible in monitoring. Reporting must
 * never itself throw or block the caller.
 */
function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_video_compress" },
    });
  } catch {
    // no-op
  }
}

/**
 * The native plugin reference, or null when unavailable. Read directly off
 * the injected Capacitor bridge (see module doc comment) rather than a
 * dynamic import, since there is no npm package to import here.
 * @returns {object|null}
 */
function getVideoCompressorPlugin() {
  try {
    const plugin =
      typeof window !== "undefined" &&
      window.Capacitor &&
      window.Capacitor.Plugins &&
      window.Capacitor.Plugins[VIDEO_COMPRESSOR_PLUGIN_NAME];
    return plugin || null;
  } catch {
    return null;
  }
}

/**
 * True only when compression could actually run: inside the Capacitor shell,
 * with the native VideoCompressor plugin registered, and the
 * `video_compression` feature flag on. All three guards live here so every
 * other function can rely on this single check.
 * @returns {boolean}
 */
export function isVideoCompressionAvailable() {
  try {
    return (
      isCapacitorEnvironment() &&
      isCapacitorPluginAvailable(VIDEO_COMPRESSOR_PLUGIN_NAME) &&
      flagOn(VIDEO_COMPRESSION_FLAG)
    );
  } catch {
    return false;
  }
}

/**
 * Build the flat telemetry object attached to the result. Always includes
 * compressed/skip_reason/elapsed_ms; adds src_* fields once a probe
 * succeeded, and out_* fields whenever a compress() call actually produced
 * an output (including the case where that output then failed post-compress
 * validation — those near-misses are exactly the "quality down" tripwire
 * this telemetry exists for). Never includes a raw file path.
 */
function buildTelemetry({ compressed, skipReason, startedAt, probe, out }) {
  const telemetry = {
    compressed,
    skip_reason: skipReason,
    elapsed_ms: Date.now() - startedAt,
  };

  if (probe) {
    telemetry.src_w = probe.widthPx;
    telemetry.src_h = probe.heightPx;
    telemetry.src_fps = probe.fps;
    telemetry.src_codec = probe.codec;
    telemetry.src_is_hdr = probe.isHdr;
    telemetry.src_bitrate_bps = probe.bitrateBps;
    telemetry.src_duration_ms = probe.durationMs;
    telemetry.src_has_audio = probe.hasAudio;
    telemetry.src_rotation = probe.rotationDegrees;
  }

  if (out) {
    telemetry.out_w = out.widthPx;
    telemetry.out_h = out.heightPx;
    telemetry.out_fps = out.fps;
    telemetry.out_codec = out.codec;
    telemetry.out_bitrate_bps = estimateOutputBitrateBps(out);
    telemetry.out_size_bytes = out.sizeBytes;
    telemetry.rotation_preserved = probe
      ? out.rotationDegrees === probe.rotationDegrees
      : null;
  }

  return telemetry;
}

// The native compress() contract (docs/native/video_compressor_plugin.md)
// includes a measured bitrateBps on the output; fall back to a size/duration
// estimate so telemetry stays populated even against an older native build
// that hasn't added the field yet.
function estimateOutputBitrateBps(out) {
  if (typeof out.bitrateBps === "number") {
    return out.bitrateBps;
  }
  if (
    typeof out.sizeBytes === "number" &&
    typeof out.durationMs === "number" &&
    out.durationMs > 0
  ) {
    return Math.round((out.sizeBytes * 8) / (out.durationMs / 1000));
  }
  return null;
}

/**
 * Build a "return the original" result with telemetry attached.
 */
function skipResult({ fileUri, sizeBytes, skipReason, startedAt, probe, out }) {
  return {
    fileUri,
    sizeBytes,
    compressed: false,
    telemetry: buildTelemetry({
      compressed: false,
      skipReason,
      startedAt,
      probe,
      out,
    }),
  };
}

/**
 * v1 trigger gate. Returns the matching skip_reason string when the source
 * should not be compressed at all, or null when it's eligible.
 */
function triggerGateSkipReason({ sizeBytes, probe }) {
  if (sizeBytes <= BELOW_SIZE_THRESHOLD_BYTES) {
    return "below_size_threshold";
  }
  if (probe.durationMs < MIN_DURATION_MS) {
    return "too_short";
  }
  if (probe.isHdr === true) {
    return "hdr_exempt_v1";
  }
  if (probe.fps > MAX_FPS_FOR_COMPRESSION) {
    return "high_fps_exempt";
  }
  const longEdge = Math.max(probe.widthPx, probe.heightPx);
  if (
    longEdge <= MAX_DIMENSION &&
    probe.bitrateBps <= targetBitrateFor(MAX_DIMENSION, probe.fps)
  ) {
    return "already_efficient";
  }
  return null;
}

/**
 * Post-compress validation. Returns the matching skip_reason string when the
 * output must be discarded in favor of the original, or null when it's safe
 * to use.
 */
function validationFailureReason({ out, sizeBytes, probe }) {
  if (
    !(
      typeof out?.sizeBytes === "number" &&
      out.sizeBytes > 0 &&
      out.sizeBytes < sizeBytes
    )
  ) {
    return "not_smaller";
  }
  const durationToleranceMs = Math.max(1000, probe.durationMs * 0.02);
  if (Math.abs(out.durationMs - probe.durationMs) > durationToleranceMs) {
    return "duration_mismatch";
  }
  if (out.rotationDegrees !== probe.rotationDegrees) {
    return "rotation_lost";
  }
  if (probe.hasAudio && !out.hasAudio) {
    return "audio_lost";
  }
  return null;
}

/**
 * Best-effort cancel of an in-flight compress() call (e.g. after it timed
 * out). Never throws — this is a kill switch, not a guarantee.
 */
async function safeCancel(plugin) {
  try {
    if (typeof plugin.cancel === "function") {
      await plugin.cancel();
    }
  } catch {
    // best-effort only
  }
}

/**
 * Best-effort delete of a compressed temp file we're about to discard (e.g.
 * because it failed post-compress validation). Never throws — a leaked temp
 * file must never mask or block the caller's result.
 */
async function safeDeleteTemp(plugin, path) {
  if (!path || typeof plugin.deleteTemp !== "function") {
    return;
  }
  try {
    await plugin.deleteTemp({ path });
  } catch {
    // best-effort only
  }
}

/**
 * Subscribe to native compress progress, if the plugin supports it. The
 * native contract does not mandate a progress event, so this is purely
 * best-effort: absence of addListener (or any failure to subscribe) just
 * means onProgress never fires, which is never itself a reason to fail
 * compression.
 * @returns {Promise<object|null>} a listener handle to remove later, or null
 */
async function wireProgressListener(plugin, onProgress) {
  if (
    typeof onProgress !== "function" ||
    typeof plugin.addListener !== "function"
  ) {
    return null;
  }
  try {
    return await plugin.addListener("progress", (event) => {
      try {
        onProgress(typeof event?.percent === "number" ? event.percent : event);
      } catch {
        // the caller's own progress handler must never break compression
      }
    });
  } catch {
    return null;
  }
}

async function removeProgressListener(handle) {
  try {
    await handle?.remove?.();
  } catch {
    // best-effort cleanup only
  }
}

/**
 * Race plugin.compress(params) against a hard timeout. Resolves to either
 * the compress() result or the COMPRESS_TIMEOUT sentinel. A synchronous
 * throw from plugin.compress is treated the same as a rejection (thrown).
 *
 * If the timeout wins the race, the compress promise is left running; a
 * `.catch(() => {})` safety net is attached up front so its eventual
 * rejection (if any) never surfaces as an unhandled promise rejection.
 */
async function compressWithTimeout(plugin, params, timeoutMs) {
  let timeoutId;
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(COMPRESS_TIMEOUT), timeoutMs);
  });

  let compressPromise;
  try {
    compressPromise = Promise.resolve(plugin.compress(params));
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
  compressPromise.catch(() => {});

  try {
    return await Promise.race([compressPromise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * The actual compression pipeline, once the caller's arguments are in hand.
 * Every branch returns a result object (never throws); the one outer
 * try/catch in compressVideoBestEffort is a last-resort safety net for
 * anything unanticipated here.
 */
async function runCompression({ fileUri, sizeBytes, onProgress, startedAt }) {
  if (!isVideoCompressionAvailable()) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "unavailable",
      startedAt,
    });
  }

  const plugin = getVideoCompressorPlugin();
  if (
    !plugin ||
    typeof plugin.probe !== "function" ||
    typeof plugin.compress !== "function"
  ) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "unavailable",
      startedAt,
    });
  }

  let probe;
  try {
    probe = await plugin.probe({ inputPath: fileUri });
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "probe_failed",
      startedAt,
    });
  }

  const gateSkipReason = triggerGateSkipReason({ sizeBytes, probe });
  if (gateSkipReason) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: gateSkipReason,
      startedAt,
      probe,
    });
  }

  const srcLongEdge = Math.max(probe.widthPx, probe.heightPx);
  const outputLongEdge = Math.min(srcLongEdge, MAX_DIMENSION);
  const compressParams = {
    inputPath: fileUri,
    maxDimension: MAX_DIMENSION,
    targetBitrateBps: targetBitrateFor(outputLongEdge, probe.fps),
    preferredCodec: capacitorPlatform() === "ios" ? "hevc" : "h264",
    preserveFps: true,
    timeoutMs: COMPRESS_TIMEOUT_MS,
  };

  const progressHandle = await wireProgressListener(plugin, onProgress);
  let out;
  try {
    const raced = await compressWithTimeout(
      plugin,
      compressParams,
      COMPRESS_TIMEOUT_MS,
    );
    if (raced === COMPRESS_TIMEOUT) {
      await safeCancel(plugin);
      return skipResult({
        fileUri,
        sizeBytes,
        skipReason: "timeout",
        startedAt,
        probe,
      });
    }
    out = raced;
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "compress_failed",
      startedAt,
      probe,
    });
  } finally {
    await removeProgressListener(progressHandle);
  }

  const failureReason = validationFailureReason({ out, sizeBytes, probe });
  if (failureReason) {
    await safeDeleteTemp(plugin, out?.outputPath);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: failureReason,
      startedAt,
      probe,
      out,
    });
  }

  return {
    fileUri: out.outputPath,
    sizeBytes: out.sizeBytes,
    compressed: true,
    telemetry: buildTelemetry({
      compressed: true,
      skipReason: null,
      startedAt,
      probe,
      out,
    }),
  };
}

/**
 * Compress a video on-device, best-effort. NEVER throws and NEVER blocks the
 * upload flow longer than the internal timeout: on any skip, failure, or
 * timeout this resolves to the ORIGINAL file with `telemetry.skip_reason`
 * set, so the caller can always just use whatever `fileUri`/`sizeBytes` it
 * gets back.
 *
 * @param {object} args
 * @param {string} args.fileUri - native `file://` path to the picked video.
 * @param {number} args.sizeBytes - size of the picked file, in bytes.
 * @param {(percent:number)=>void} [args.onProgress] - optional 0-100 progress
 *   callback; best-effort, may never fire (see wireProgressListener).
 * @returns {Promise<{fileUri:string, sizeBytes:number, compressed:boolean, telemetry:object}>}
 */
export async function compressVideoBestEffort(args) {
  const startedAt = Date.now();
  const fileUri = args?.fileUri;
  const sizeBytes = args?.sizeBytes;
  const onProgress = args?.onProgress;

  try {
    return await runCompression({ fileUri, sizeBytes, onProgress, startedAt });
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "error",
      startedAt,
    });
  }
}

export default {
  isVideoCompressionAvailable,
  compressVideoBestEffort,
};
